import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { ServiceRecommendation } from '@/lib/customers/types';
// DTC-J: route the service-recommendation `resolved_at` and
// `converted_at` write timestamps through the canonical clock so the
// persisted lifecycle audit trail is deterministic under
// `clock.setFixed(...)`.
import { clock } from '@/utils/clock';

type RecommendationStatus = 'recommended' | 'approved' | 'declined' | 'completed' | 'expired';

interface CreateRecommendationInput {
  customer_id: string;
  vehicle_id: string;
  source_type: 'dvi' | 'advisor' | 'tech' | 'optiqueue' | 'other';
  source_ticket_id?: string | null;
  source_finding_group_id?: string | null;
  job_catalog_id?: string | null;
  title: string;
  description?: string | null;
  notes?: string | null;
  severity?: 'info' | 'soon' | 'urgent';
}

interface UpdateRecommendationInput {
  id: string;
  status?: RecommendationStatus;
  notes?: string | null;
  resolved_at?: string | null;
  converted_ticket_id?: string | null;
  converted_at?: string | null;
}

/**
 * Hook for fetching recommendations for a customer or vehicle
 */
export function useServiceRecommendations(
  customerId?: string,
  vehicleId?: string,
  statusFilter?: RecommendationStatus[]
) {
  const { activeShop } = useAuth();

  return useQuery({
    queryKey: ['service-recommendations', customerId, vehicleId, statusFilter],
    queryFn: async (): Promise<ServiceRecommendation[]> => {
      if (!activeShop?.id) return [];
      if (!customerId && !vehicleId) return [];

      // R125-M-4: narrow select — mirrors the explicit ServiceRecommendation
      // interface (src/lib/customers/types.ts:40). service_recommendations
      // has no PII today, but select('*') would auto-expose any future
      // internal columns (ML scores, operator notes, etc.) to the client
      // cache. Same fix as R30-M-1 on useCustomer.ts.
      let query = supabase
        .from('service_recommendations')
        .select('id, shop_id, customer_id, vehicle_id, source_type, source_ticket_id, source_finding_group_id, job_catalog_id, title, description, notes, severity, status, recommended_at, resolved_at, expires_at, converted_ticket_id, converted_at, created_by, resolved_by, created_at, updated_at')
        .eq('shop_id', activeShop.id)
        .order('recommended_at', { ascending: false });

      if (customerId) {
        query = query.eq('customer_id', customerId);
      }

      if (vehicleId) {
        query = query.eq('vehicle_id', vehicleId);
      }

      if (statusFilter && statusFilter.length > 0) {
        query = query.in('status', statusFilter);
      }

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as unknown as ServiceRecommendation[];
    },
    enabled: !!activeShop?.id && (!!customerId || !!vehicleId),
  });
}

/**
 * Hook for recommendation CRUD operations
 */
export function useRecommendationMutations() {
  const { activeShop } = useAuth();
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: async (input: CreateRecommendationInput): Promise<ServiceRecommendation> => {
      if (!activeShop?.id) throw new Error('No active shop');

      const { data, error } = await supabase
        .from('service_recommendations')
        .insert({
          shop_id: activeShop.id,
          status: 'recommended',
          severity: 'info',
          ...input,
        })
        .select()
        .single();

      if (error) throw error;
      return data as unknown as ServiceRecommendation;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['service-recommendations', data.customer_id] });
      queryClient.invalidateQueries({ queryKey: ['service-recommendations', undefined, data.vehicle_id] });
      queryClient.invalidateQueries({ queryKey: ['customer', data.customer_id] });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (input: UpdateRecommendationInput): Promise<ServiceRecommendation> => {
      const { id, ...updates } = input;

      const { data, error } = await supabase
        .from('service_recommendations')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as unknown as ServiceRecommendation;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['service-recommendations', data.customer_id] });
      queryClient.invalidateQueries({ queryKey: ['service-recommendations', undefined, data.vehicle_id] });
      queryClient.invalidateQueries({ queryKey: ['customer', data.customer_id] });
    },
  });

  const markCompletedMutation = useMutation({
    mutationFn: async ({ id, convertedTicketId }: { id: string; convertedTicketId?: string }): Promise<ServiceRecommendation> => {
      const { data, error } = await supabase
        .from('service_recommendations')
        .update({
          status: 'completed',
          resolved_at: clock.now().toISOString(),
          converted_ticket_id: convertedTicketId || null,
          converted_at: convertedTicketId ? clock.now().toISOString() : null,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as unknown as ServiceRecommendation;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['service-recommendations'] });
      queryClient.invalidateQueries({ queryKey: ['customer', data.customer_id] });
    },
  });

  const markDeclinedMutation = useMutation({
    mutationFn: async (id: string): Promise<ServiceRecommendation> => {
      const { data, error } = await supabase
        .from('service_recommendations')
        .update({
          status: 'declined',
          resolved_at: clock.now().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as unknown as ServiceRecommendation;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['service-recommendations'] });
      queryClient.invalidateQueries({ queryKey: ['customer', data.customer_id] });
    },
  });

  return {
    createRecommendation: createMutation.mutateAsync,
    updateRecommendation: updateMutation.mutateAsync,
    markCompleted: markCompletedMutation.mutateAsync,
    markDeclined: markDeclinedMutation.mutateAsync,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
  };
}
